import { useEmblaCarousel } from 'embla-carousel/react';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from 'styled-components';
import useRecursiveTimeout from '@insite/client-framework/Common/Hooks/useRecursiveTimeout';
import StyledWrapper from '@insite/client-framework/Common/StyledWrapper';
import Button, { ButtonIcon } from '@insite/mobius/Button';
import BannerArrow from '@NA/Icons/BannerArrow';
import { BannerCarouselList } from '@NA/Services/GetBannerCarousel';
import ADIColors from '@NA/Utils/ADIColors';

const EmblaCarouselWrapper = ({
    fields,
    styles,
    carouselList,
    children,
}: {
    fields: any;
    styles: any;
    carouselList: BannerCarouselList[];
    children: ReactNode;
}) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) return;
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) return;
        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !carouselList || carouselList.length === 0) return;

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, carouselList]);

    return (
        <>
            {fields.showArrows && carouselList.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon {...styles.iconProps} src={BannerArrow} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                {children}
            </StyledWrapper>
            {fields.slideIndicator && carouselList.length > 1 && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    {(carouselList as BannerCarouselList[]).map((_: any, i: number) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background:  ${ADIColors.blue2};
                                    &:hover { background: ${ADIColors.white}; }
                                    &:focus { background: ${ADIColors.blue2}; }
                                    &:active { background: ${ADIColors.blue2}; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        return <Button key={i} {...dotButtonStyles} onClick={() => embla && embla.scrollTo(i)} />;
                    })}
                </StyledWrapper>
            )}
            {fields.showArrows && carouselList.length > 1 && (
                <StyledWrapper {...styles.nextArrowWrapper}>
                    <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                        <ButtonIcon {...styles.iconProps} src={BannerArrow} />
                    </Button>
                </StyledWrapper>
            )}
        </>
    );
};

export default EmblaCarouselWrapper;
